import React from 'react';
import { Button, Label, Icon } from '@repo/ui';

interface ConditionRule {
  id: string;
  fieldId: number;
  fieldName: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'in' | 'not_in' | 'greater_than' | 'less_than';
  value: string | string[] | null;
}

interface CustomFieldOption {
  id: number;
  name: string;
  field_type: string;
  parent_type: string;
  picklist_options?: Array<{ id: number; option: string }>;
}

interface ConditionRuleBuilderProps {
  condition: ConditionRule;
  customFields: CustomFieldOption[];
  onChange: (updates: Partial<ConditionRule>) => void;
  onRemove: () => void;
}

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Not Contains' },
  { value: 'in', label: 'In (comma-separated)' },
  { value: 'not_in', label: 'Not In (comma-separated)' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
];

export function ConditionRuleBuilder({
  condition,
  customFields,
  onChange,
  onRemove,
}: ConditionRuleBuilderProps) {
  const selectedField = customFields.find(f => f.id === condition.fieldId);
  
  const handleFieldChange = (fieldId: number) => {
    const field = customFields.find(f => f.id === fieldId);
    if (field) {
      onChange({
        fieldId: field.id,
        fieldName: field.name,
      });
    }
  };

  const handleOperatorChange = (operator: ConditionRule['operator']) => {
    // Reset value if switching between single/array types
    const isArrayOperator = operator === 'in' || operator === 'not_in';
    const currentIsArray = Array.isArray(condition.value);
    
    if (isArrayOperator !== currentIsArray) {
      onChange({ operator, value: isArrayOperator ? [] : '' });
    } else {
      onChange({ operator });
    }
  };

  const handleValueChange = (newValue: string) => {
    const isArrayOperator = condition.operator === 'in' || condition.operator === 'not_in';
    
    if (isArrayOperator) {
      // Split by comma and trim
      const arrayValue = newValue.split(',').map(v => v.trim()).filter(v => v);
      onChange({ value: arrayValue });
    } else {
      onChange({ value: newValue });
    }
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(opt => opt.value);
    onChange({ value: selectedOptions });
  };

  const displayValue = Array.isArray(condition.value) 
    ? condition.value.join(', ') 
    : condition.value;

  // Determine if we should use a dropdown based on field type
  const isCheckboxField = selectedField?.field_type?.toLowerCase() === 'checkbox';
  const isPicklistField = selectedField?.field_type?.toLowerCase() === 'picklist' || 
                          selectedField?.field_type?.toLowerCase() === 'pick_list';
  const hasPicklistOptions = isPicklistField && selectedField?.picklist_options && selectedField.picklist_options.length > 0;
  const isArrayOperator = condition.operator === 'in' || condition.operator === 'not_in';

  // Render appropriate value input based on field type
  const renderValueInput = () => {
    // Checkbox field - show true/false dropdown
    if (isCheckboxField) {
      return (
        <select
          value={Array.isArray(condition.value) ? condition.value[0] || '' : condition.value || ''}
          onChange={e => onChange({ value: e.target.value })}
          className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-600 text-xtnd-dark dark:text-xtnd-white focus:outline-none focus:ring-2 focus:ring-xtnd-blue"
        >
          <option value="">Select Value</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    // Picklist field with 'in' or 'not_in' operator - show multi-select
    if (hasPicklistOptions && isArrayOperator) {
      const selectedValues = Array.isArray(condition.value) ? condition.value : [];
      return (
        <select
          multiple
          value={selectedValues}
          onChange={handleMultiSelectChange}
          className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-600 text-xtnd-dark dark:text-xtnd-white focus:outline-none focus:ring-2 focus:ring-xtnd-blue min-h-[80px]"
          title="Hold Ctrl/Cmd to select multiple options"
        >
          {selectedField?.picklist_options?.map(option => (
            <option key={option.id} value={option.option}>
              {option.option}
            </option>
          ))}
        </select>
      );
    }

    // Picklist field with other operators - show single-select dropdown
    if (hasPicklistOptions && !isArrayOperator) {
      // Check if operator supports NULL checks (equals, not_equals)
      const supportsNull = condition.operator === 'equals' || condition.operator === 'not_equals';
      
      // Normalize the value for display (handle null, __NULL__, etc.)
      const currentValue = Array.isArray(condition.value) 
        ? condition.value[0] 
        : condition.value;
      
      // Check for null or missing values (undefined often results from backend stripping nulls)
      const isNullOrMissing = currentValue === null || currentValue === undefined || currentValue === '__NULL__';
      
      // When value is null (or missing) and null is supported, use __NULL__ for display
      // Otherwise, use empty string for "Select Value" or the actual value
      const displayValue = isNullOrMissing && supportsNull
        ? '__NULL__'
        : currentValue === null || currentValue === undefined || currentValue === ''
          ? ''
          : currentValue;
      
      return (
        <select
          value={displayValue}
          onChange={e => {
            // Convert __NULL__ to null for NULL checks, keep empty string as empty string
            const selectedValue = e.target.value === '__NULL__'
              ? null 
              : e.target.value;
            onChange({ value: selectedValue });
          }}
          className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-600 text-xtnd-dark dark:text-xtnd-white focus:outline-none focus:ring-2 focus:ring-xtnd-blue"
        >
          <option value="">Select Value</option>
          {supportsNull && (
            <option value="__NULL__">(NULL / Empty)</option>
          )}
          {selectedField?.picklist_options?.map(option => (
            <option key={option.id} value={option.option}>
              {option.option}
            </option>
          ))}
        </select>
      );
    }

    // Default - text input for all other field types
    return (
      <input
        type="text"
        value={displayValue}
        onChange={e => handleValueChange(e.target.value)}
        placeholder={
          isArrayOperator
            ? 'Value1, Value2, Value3'
            : 'Value'
        }
        className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-600 text-xtnd-dark dark:text-xtnd-white focus:outline-none focus:ring-2 focus:ring-xtnd-blue"
      />
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 p-3 bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-md border border-xtnd-dark-100 dark:border-xtnd-dark-600">
        {/* Field Selector */}
        <div className="flex-1 min-w-0">
        <select
          value={condition.fieldId || ''}
          onChange={e => handleFieldChange(Number(e.target.value))}
          className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-600 text-xtnd-dark dark:text-xtnd-white focus:outline-none focus:ring-2 focus:ring-xtnd-blue"
        >
          <option value="">Select Custom Field</option>
          {customFields.map(field => (
            <option key={field.id} value={field.id}>
              {field.name}
            </option>
          ))}
        </select>
      </div>

      {/* Operator Selector */}
      <div className="flex-1 min-w-0">
        <select
          value={condition.operator}
          onChange={e => handleOperatorChange(e.target.value as ConditionRule['operator'])}
          className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-600 text-xtnd-dark dark:text-xtnd-white focus:outline-none focus:ring-2 focus:ring-xtnd-blue"
        >
          {OPERATORS.map(op => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </select>
      </div>

      {/* Value Input */}
      <div className="flex-1 min-w-0">
        {renderValueInput()}
      </div>

        {/* Remove Button */}
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
          title="Remove condition"
        >
          <Icon name="HiX" className="w-5 h-5" />
        </button>
      </div>
      
      {/* Helper text for multi-select picklist */}
      {hasPicklistOptions && isArrayOperator && (
        <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 ml-3">
          💡 Hold <kbd className="px-1 py-0.5 bg-xtnd-dark-100 dark:bg-xtnd-dark-600 rounded text-xs">Ctrl</kbd> (Windows) or{' '}
          <kbd className="px-1 py-0.5 bg-xtnd-dark-100 dark:bg-xtnd-dark-600 rounded text-xs">Cmd</kbd> (Mac) to select multiple options
        </p>
      )}
    </div>
  );
}

